import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import { loginUser, logoutUser } from '../../actions/user';
import { addCartItems, updateBooker } from '../../actions/cart';
import {
    bookerFieldsFromTickets,
    getBookerFieldDefaults,
    getBookingGuestData,
    withGuestStayDates
} from '../../utils';

function JwtOauthLogin({ loginUserAction, logoutUserAction, updateBookingGuest, fillCart }) {
    const location = useLocation();
    const history = useHistory();

    const [error, setError] = useState(null);
    const [handled, setHandled] = useState(false);

    // Extract the token WITHOUT URLSearchParams: it decodes `+` to a space, and a
    // partner that signs with standard base64 (rather than base64url) puts `+`
    // straight into the token — the space then breaks verification and the
    // hand-off 401s. Decode percent-escapes only.
    const rawToken = (location.search.match(/[?&]token=([^&]*)/) || [])[1];
    let jwtOauthToken = null;
    if (rawToken) {
        try {
            jwtOauthToken = decodeURIComponent(rawToken);
        } catch (err) {
            jwtOauthToken = rawToken;
        }
    }

    // Call the jwt-oauth-login API ONLY if token exists
    const [response, loading, apiError] = useSwaggerClient(
        'auth',
        'jwtOauthAuth',
        jwtOauthToken ? { jwtOauthToken } : null
    );

    useEffect(() => {
        // Always logout first
        logoutUserAction();
        if (!jwtOauthToken) {
            setError('Missing JWT OAuth token');
        }
    }, [jwtOauthToken, logoutUserAction]);

    useEffect(() => {
        if (handled || loading) {
            return;
        }

        // API-level error
        if (apiError) {
            let message = 'JWT OAuth login failed';
            // try parse swagger client error
            try {
                const rawError = apiError.response.data;
                const parsedError = JSON.parse(rawError);

                if (rawError && parsedError && parsedError.message) {
                    message = parsedError.message;
                }
                if (rawError && parsedError.details && parsedError.details.message) {
                    message = parsedError.details.message;
                }
            } catch (error) {
                // ignore parsing error
                console.error('Error parsing API error response', error);
            }

            setError(message);
            setHandled(true);
            return;
        }

        // Successful login
        if (response.user) {
            if (
                !response.flow ||
                (response.flow.type !== 'cancel' && response.flow.type !== 'checkout')
            ) {
                setError('Unsupported flow type');
            }
            loginUserAction(response.user);

            const itemsWithTickets =
                response.flow.type === 'checkout'
                    ? (response.flow.cartItems || []).filter(
                          item => item.tickets && item.tickets.length > 0
                      )
                    : [];
            const allTickets = itemsWithTickets.map(item => item.tickets).flat();

            // The partner token carries activity + ticket ids only, so anything a
            // guest would normally type has to come from their profile — the same
            // prefill the in-app add-to-cart paths run. Without it the required
            // Arrival / Departure Date booker fields land empty and the guest is
            // stuck on "There are some missing fields for this activity".
            updateBookingGuest({
                ...getBookerFieldDefaults(allTickets),
                ...getBookingGuestData(response.user, bookerFieldsFromTickets(allTickets))
            });

            if (itemsWithTickets.length > 0) {
                fillCart(itemsWithTickets.map(item => withGuestStayDates(item, response.user)));
            }
            const redirectTo = response.redirectUrl || '/';
            setHandled(true);
            history.replace(redirectTo);
            return;
        }

        // Logical error from backend
        if (response && !response.user) {
            setError(response.error?.message || 'JWT OAuth login failed');
            setHandled(true);
        }
    }, [
        loading,
        response,
        apiError,
        handled,
        loginUserAction,
        history,
        fillCart,
        updateBookingGuest
    ]);

    if (loading && !error) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: '100vh' }}
            >
                <Grid item style={{ textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography variant="h6" style={{ marginTop: 20 }}>
                        Logging in…
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    // Error state
    if (error) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: '100vh' }}
            >
                <Grid item>
                    <Typography variant="h5" color="error">
                        Sorry, we could not log you in automatically.
                    </Typography>
                    <Typography variant="body1" style={{ marginTop: 10 }}>
                        Please try logging in manually or contact support if the issue persists.
                    </Typography>
                    <h5 style={{ marginTop: 20 }}>
                        Error details:
                        <br />
                        <br />
                        {error}
                    </h5>
                </Grid>
            </Grid>
        );
    }

    // Fallback
    return null;
}

const mapStateToProps = state => ({
    user: state.user.user,
    cart: state.cart
});

const mapDispatchToProps = dispatch => ({
    fillCart: cartItems => {
        dispatch(addCartItems(cartItems));
    },
    logoutUserAction: () => {
        dispatch(logoutUser());
    },
    loginUserAction: user => dispatch(loginUser(user)),
    updateBookingGuest: guest => {
        dispatch(updateBooker(guest));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(JwtOauthLogin);
